// ─── Drive-time isochrone ─────────────────────────────────────────────────
// Google has no isochrone API, so the reachable area is approximated: cast
// rays out from the origin, binary-search along each one with Distance Matrix
// to find where the target drive time falls, then join those points into a
// ring. That ring feeds the same pointInPolygon filter the drawn boundary
// uses, so the whole downstream path is already built.
//
// This uses the Maps JavaScript API's DistanceMatrixService, NOT the REST
// endpoint. The REST endpoint sends no CORS headers and is blocked in the
// browser — services/googleMaps.js drivingTimes() calls it via fetch and
// cannot work from here.
//
// Cost is fixed at rays x refinements elements per query (default 16 x 4 =
// 64), regardless of how many pins are on the map.
//
// Accuracy caveat, deliberately surfaced in the UI: this is a geometric
// approximation. Rays that cross water or run out of road return no route;
// those are clamped to the last known-good radius rather than collapsing the
// ring inward. On a coastline — Naples, Fort Myers, Tampa Bay — the shape
// will visibly overstate reachability across the water.

const EARTH_M = 6371000;
const DEFAULT_RAYS = 16;
const DEFAULT_REFINEMENTS = 4;
// Upper bound for the search: nothing reaches further than this in `minutes`.
const MAX_MPH = 70;

function destinationPoint(lat, lng, bearingDeg, meters) {
  const br = (bearingDeg * Math.PI) / 180;
  const la1 = (lat * Math.PI) / 180;
  const lo1 = (lng * Math.PI) / 180;
  const dr = meters / EARTH_M;
  const la2 = Math.asin(Math.sin(la1) * Math.cos(dr) + Math.cos(la1) * Math.sin(dr) * Math.cos(br));
  const lo2 = lo1 + Math.atan2(
    Math.sin(br) * Math.sin(dr) * Math.cos(la1),
    Math.cos(dr) - Math.sin(la1) * Math.sin(la2)
  );
  return [(la2 * 180) / Math.PI, (((lo2 * 180) / Math.PI) + 540) % 360 - 180];
}

// Promisified DistanceMatrixService for one origin and many destinations.
// Caps at 25 destinations per request, which is the service limit.
function durationsFrom(origin, points) {
  const g = window.google && window.google.maps;
  if (!g || !g.DistanceMatrixService) {
    return Promise.reject(new Error("Google Maps JS API not loaded"));
  }
  const svc = new g.DistanceMatrixService();
  return new Promise((resolve, reject) => {
    svc.getDistanceMatrix(
      {
        origins: [new g.LatLng(origin.lat, origin.lng)],
        destinations: points.map(([lat, lng]) => new g.LatLng(lat, lng)),
        travelMode: g.TravelMode.DRIVING,
        unitSystem: g.UnitSystem.IMPERIAL,
      },
      (res, status) => {
        if (status !== "OK" || !res?.rows?.[0]) {
          reject(new Error(`Distance Matrix: ${status}`));
          return;
        }
        resolve(res.rows[0].elements.map(el =>
          el && el.status === "OK" && el.duration ? el.duration.value : null));
      }
    );
  });
}

/**
 * Build an approximate drive-time ring.
 *
 * @param {{lat:number,lng:number}} origin
 * @param {number} minutes  target drive time
 * @param {{rays?:number, refinements?:number, onProgress?:Function}} opts
 * @returns {Promise<{ring:number[][], elementsUsed:number, unreachableRays:number}>}
 */
export async function buildIsochrone(origin, minutes, opts = {}) {
  const rays = opts.rays || DEFAULT_RAYS;
  const refinements = opts.refinements || DEFAULT_REFINEMENTS;
  const targetSec = minutes * 60;

  const maxMeters = (minutes / 60) * MAX_MPH * 1609.344;
  const bearings = Array.from({ length: rays }, (_, i) => (360 / rays) * i);

  // Per-ray binary search bounds, all advanced in lockstep so each refinement
  // is a single batched request.
  let lo = bearings.map(() => 0);
  let hi = bearings.map(() => maxMeters);
  let elementsUsed = 0;

  for (let step = 0; step < refinements; step++) {
    const mids = lo.map((l, i) => (l + hi[i]) / 2);
    const points = mids.map((m, i) => destinationPoint(origin.lat, origin.lng, bearings[i], m));

    // 25-destination cap per request.
    const durations = [];
    for (let i = 0; i < points.length; i += 25) {
      const chunk = points.slice(i, i + 25);
      // eslint-disable-next-line no-await-in-loop
      const d = await durationsFrom(origin, chunk);
      durations.push(...d);
      elementsUsed += chunk.length;
    }

    for (let i = 0; i < mids.length; i++) {
      const sec = durations[i];
      if (sec == null) {
        // No route — water, or off the road network. Treat as unreachable and
        // pull the ceiling in rather than letting the ray run away.
        hi[i] = mids[i];
      } else if (sec <= targetSec) {
        lo[i] = mids[i];
      } else {
        hi[i] = mids[i];
      }
    }
    if (opts.onProgress) opts.onProgress((step + 1) / refinements);
  }

  // A ray that never found a reachable point sits at 0. Rather than pinching
  // the ring to the origin, borrow the median of the rays that did resolve —
  // an honest-looking shape beats a star-shaped artefact.
  const resolved = lo.filter(v => v > 0).sort((a, b) => a - b);
  const unreachableRays = lo.length - resolved.length;
  const median = resolved.length > 0 ? resolved[Math.floor(resolved.length / 2)] : 0;
  const radii = lo.map(v => (v > 0 ? v : median));

  const ring = radii.map((m, i) => destinationPoint(origin.lat, origin.lng, bearings[i], m));

  return { ring, elementsUsed, unreachableRays };
}

// What a query will cost before it runs, so the UI can say so up front.
export function estimateElements(rays = DEFAULT_RAYS, refinements = DEFAULT_REFINEMENTS) {
  return rays * refinements;
}
